import "../styles/default.scss";

import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { withRouter } from "react-router";
import { compose } from "redux";
import SweetScroll from "sweet-scroll";

import { UrlProvider } from "../../../../../api/UrlProvider";
import config from "../../../../../config";
import { LABEL_TYPES } from "../../../../../lib/label/labelTypes";
import withCollapsibleSection from "../../../../../hocs/withCollapsibleSection";
import withGlobalConfigFields from "../../../../../hocs/withGlobalConfigFields";
import withStockBasicInfo from "../../../../../hocs/withStockBasicInfo";
import withUserData from "../../../../../hocs/withUserData";
import { getStockPageSectionTriggerUrl } from "../../../../../lib/getStockPageSectionTriggerUrl";
import { showModal } from "../../../../../lib/react-redux-modal-provider";
import { globalConfigFields } from "../../../../../redux/modules/globalConfig";
import SquaberLink from "../../SquaberLink/modules/default";
import { SectionHeader } from "../components/sectionHeader";

const StockPageSection = ({
  title,
  children,
  label,
  sectionId,
  sectionPermissions = [],
  isAiGenerated = false,
  canDisplaySection,
  ticker,
  locale,
  market,
  slug,
  stock,
  match: {
    params: { section: currentSection }
  },
  translate,
  translationExists,
  stockBasicInfo,
  userAccessLevel,
  sectionLinkTo
}) => {
  const shouldShowPremiumNotice = !canDisplaySection(sectionPermissions);
  const navigatesAway = Boolean(sectionLinkTo);

  const sectionRef = useRef(null);

  const urlPattern = "fe.stockPageSection";

  const urlProviderVariables = {
    locale,
    marketId: market,
    stockId: ticker.toLowerCase(),
    section: slug
  };

  const canonicalUrl =
    !navigatesAway && slug !== sectionId
      ? UrlProvider.getUrl(urlPattern, urlProviderVariables, config.webAppUrl)
      : null;

  const sectionOpen = useMemo(() => {
    if (navigatesAway) {
      return false;
    }

    return [slug, sectionId].includes(currentSection);
  }, [currentSection, slug, sectionId, navigatesAway]);

  const triggerUrl = useMemo(
    () =>
      sectionLinkTo ||
      getStockPageSectionTriggerUrl({
        sectionOpen,
        locale,
        market,
        ticker,
        slug
      }),
    [sectionLinkTo, sectionOpen, locale, market, ticker, slug]
  );

  useEffect(() => {
    if (!sectionOpen) {
      return undefined;
    }

    const sweetScroll = new SweetScroll({
      duration: 500,
      vertical: true,
      updateURL: false
    });

    // Wait for content above (chart, lazy sections) to settle before scrolling.
    const SECTION_SCROLL_DELAY_MS = 1000;
    const timeoutId = setTimeout(() => {
      if (sectionRef.current) {
        sweetScroll.toElement(sectionRef.current, {
          offset: -100
        });
      }
    }, SECTION_SCROLL_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
      sweetScroll.destroy();
    };
  }, [currentSection, sectionOpen]);

  const fullName = stockBasicInfo.fullname
    ? stockBasicInfo.fullname
    : stock?.stock?.name;

  const sectionDescriptionTranslationKey = `${sectionId}__description`;

  if (shouldShowPremiumNotice) {
    return (
      <div
        id={sectionId}
        className={classNames({
          "squaber-stock-page-section": true
        })}
        ref={sectionRef}
      >
        <div className="Collapsible">
          <button
            onClick={() => {
              if (!canDisplaySection(sectionPermissions)) {
                showModal("PremiumModal", {
                  skipShowIfAlreadyVisible: true,
                  permissions: sectionPermissions
                });
              }
            }}
            className={"Collapsible__trigger"}
          >
            <SectionHeader
              title={title}
              shouldShowPremiumNotice={shouldShowPremiumNotice}
              sectionPermissions={sectionPermissions}
              isAiGenerated={isAiGenerated}
              fullName={fullName}
              label={label}
            />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {sectionOpen && !!canonicalUrl ? (
        <Helmet>
          <link rel="canonical" key="canonical" href={canonicalUrl} />
        </Helmet>
      ) : null}
      <div
        id={sectionId}
        className={classNames({
          "squaber-stock-page-section": true
        })}
        ref={sectionRef}
      >
        <div className="Collapsible">
          <SquaberLink
            to={triggerUrl}
            skipScrollToTop={!navigatesAway}
            className={classNames({
              Collapsible__trigger: true,
              "is-open": sectionOpen
            })}
          >
            <SectionHeader
              title={title}
              shouldShowPremiumNotice={shouldShowPremiumNotice}
              sectionPermissions={sectionPermissions}
              isAiGenerated={isAiGenerated}
              fullName={fullName}
              label={label}
            />
          </SquaberLink>
          <div
            className={classNames({
              Collapsible__contentOuter: true,
              "is-open": sectionOpen
            })}
          >
            {sectionOpen ? (
              <React.Fragment>
                {translationExists(sectionDescriptionTranslationKey) ? (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: translate(sectionDescriptionTranslationKey, {
                        stock__ticker: ticker,
                        stock__market: market,
                        stock__short_name: stock?.stock?.short_name
                      })
                    }}
                  />
                ) : null}
                {children}
              </React.Fragment>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

StockPageSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.any,
  sectionId: PropTypes.string,
  label: PropTypes.shape({
    type: PropTypes.oneOf(LABEL_TYPES),
    text: PropTypes.string
  }),
  isAiGenerated: PropTypes.bool,
  alwaysOpen: PropTypes.bool,
  sectionLinkTo: PropTypes.string
};

export default compose(
  withUserData,
  withStockBasicInfo,
  withCollapsibleSection,
  withRouter,
  withGlobalConfigFields([globalConfigFields.OFFER_URL])
)(StockPageSection);
